import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { InboxIcon, InboxSlashIcon } from "modules/icons";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminEditDistributionGroupMutation,
  InternalAdminEditDistributionGroupMutationVariables,
} from "gql/graphql";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { useMutation } from "@apollo/client";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-edit-distribution-group";
import { Spinner } from "@chakra-ui/react";

interface IProps {
  distributionGroup?: InternalAdminDistributionGroupFragment;
  userId: string;
}

const AdminToggleNotificationsDistributionGroupButton = ({
  distributionGroup,
  userId,
}: IProps) => {
  const [internalAdminEditDistributionGroupMutationAPI, { loading }] =
    useMutation<
      InternalAdminEditDistributionGroupMutation,
      InternalAdminEditDistributionGroupMutationVariables
    >(INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION, {
      fetchPolicy: "network-only",
    });

  const onClick = useCallback(async () => {
    if (!distributionGroup) return;
    const enableNotifications =
      !distributionGroup.notificationUserIdList.includes(userId);
    await internalAdminEditDistributionGroupMutationAPI({
      variables: {
        organizationId: distributionGroup.organizationId,
        id: distributionGroup.id,
        input: {
          notificationUserIdListToAdd: enableNotifications
            ? [userId]
            : undefined,
          notificationUserIdListToRemove: enableNotifications
            ? undefined
            : [userId],
        },
      },
    });
  }, [
    distributionGroup,
    internalAdminEditDistributionGroupMutationAPI,
    userId,
  ]);

  const ariaLabel = distributionGroup?.notificationUserIdList.includes(userId)
    ? "notifications on"
    : "notifications off";

  const icon = useMemo(() => {
    if (loading) {
      return <Spinner size="sm" />;
    }
    if (distributionGroup?.notificationUserIdList.includes(userId)) {
      return (
        <SyncoTooltip label="Group messages sent to Threads Inbox">
          <InboxIcon fill={colors.blue} boxSize={5} />
        </SyncoTooltip>
      );
    }
    return (
      <SyncoTooltip label="Group messages not sent to Threads Inbox">
        <InboxSlashIcon fill={colors.grayMain} boxSize={5} />
      </SyncoTooltip>
    );
  }, [distributionGroup?.notificationUserIdList, loading, userId]);

  if (!distributionGroup) {
    return null;
  }

  return (
    <CircularIconButton
      aria-label={ariaLabel}
      variant="ghost"
      onClick={onClick}
      icon={icon}
    />
  );
};

export default memo(AdminToggleNotificationsDistributionGroupButton);
